import { ImageOffIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { fetchBusinessCardImage } from '@/api/businessCards'
import { Skeleton } from '@/components/ui/skeleton'

/**
 * A card image, fetched with the caller's token and handed to the browser as an object URL.
 *
 * `<img src="/api/…">` cannot work here: the browser sends no `Authorization` header on an image
 * request, and the endpoint is behind the lead's visibility rule for good reason. So the bytes come
 * through `fetch` and the URL is revoked when the component goes away — an object URL that is never
 * revoked keeps its blob in memory for the life of the page.
 */
export function BusinessCardImage({
  leadId,
  contactId,
  cardId,
  variant = 'THUMBNAIL',
  alt,
  className,
}: {
  leadId: string
  contactId: string
  cardId: string
  variant?: 'ORIGINAL' | 'THUMBNAIL'
  alt: string
  className?: string
}) {
  const { t } = useTranslation()
  const [url, setUrl] = useState<string | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    const controller = new AbortController()
    let objectUrl: string | null = null

    fetchBusinessCardImage(leadId, contactId, cardId, variant, controller.signal)
      .then((blob) => {
        if (controller.signal.aborted) {
          return
        }
        objectUrl = URL.createObjectURL(blob)
        setUrl(objectUrl)
      })
      .catch(() => {
        if (!controller.signal.aborted) {
          setFailed(true)
        }
      })

    return () => {
      controller.abort()
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
      }
    }
  }, [leadId, contactId, cardId, variant])

  if (failed) {
    return (
      <div
        className={`text-muted-foreground flex flex-col items-center justify-center gap-1 rounded-md border border-dashed text-xs ${className ?? ''}`}
      >
        <ImageOffIcon className="size-4" aria-hidden />
        {t('businessCards.imageFailed')}
      </div>
    )
  }

  if (!url) {
    return <Skeleton className={className} />
  }

  return <img src={url} alt={alt} className={`rounded-md border object-cover ${className ?? ''}`} />
}
